import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AlertToast from '@components/AlertToast/AlertToast';
import Button from '@components/Button/Button';
import LoadingScreen from '@components/LoadingScreen/LoadingScreen';
import PaginationComponent from '@components/Pagination/Pagination';
import TableReportAutorization from '@components/TableReportAutorization/TableReportAutorization';
import { useWFService } from '@services/useWorkflow';
import { useInstitutionsService } from '@services/useInstitutions';
import { useAppContext } from '@context/AppContext';
import { useDrugsService } from '@services/useDrugs';
import FilterConciliation from '@components/FilterConciliation/FilterConciliation';
import { adjustStartAndEndDate } from 'utils/GlobalFunction';
import { jwtDecode } from "jwt-decode";
import useAutoRefresh from "@hooks/useAutoRefresh";
import { useFileExport } from '@services/useFileExport';
import NoAccess from '@components/NoAccess/NoAccess';
import styles from './ReportAutorizationPage.module.scss';

interface DecodedToken {
    org_code: number;
    [key: string]: any;
}
interface PageProps {
    setOptionMenu: (codeMenu: string) => void;
    setBreadcrumb: (breadcrumb: any) => void;
}

const ReportAutorizationPage: React.FC<PageProps> = ({ setOptionMenu, setBreadcrumb }) => {
    const { getTasks , getAllStages } = useWFService();
    const { getInstitutions } = useInstitutionsService();
    const { state } = useAppContext();
    const { user , token } = state;
    const { getAllDrugs } = useDrugsService();
    const [loadingState, setLoadingState] = useState(false);
    const { reportAutorizationExcel } = useFileExport();
    const [loadingMessage, setLoadingMessage] = useState('Buscando solicitudes...');
    const [alertToastMessage, setAlertToastMessage] = useState('');
    const [alertToastState , setAlertToastState] = useState(false);
    

    const [alertToastIcon, setAlertToastIcon] = useState<'success' | 'error' | 'warning' | 'info'>('success');
    const [firstCall , setFirstCall] = useState(true);
    const [tasksList, setTasksList] = useState([]);
    const navigate = useNavigate();
    const [noAccess, setNoAccess] = useState(false);
    const [paginationData, setPaginationData] = useState({
        limit: 10,
        skip: 0,
        totalCount: 0
    });

    const [ stagesList , setStagesList ] = useState<any>([]);
    const [ institutionsList , setInstitutionsList ] = useState<any[]>([]);
    const [ drugsList , setDrugsList ] = useState<any[]>([]);
    const [administrationDateRange, setAdministrationDateRange] = useState({
        startDate: new Date(), 
        endDate: new Date()
    });
    const [searchValues, setSearchValues] = useState<any>({
        patientSearch: [],
        administrationStart: administrationDateRange.startDate.setHours(0, 0, 0, 0),
        administrationEnd: administrationDateRange.endDate.setHours(0, 0, 0, 0),
        elaborationStart: new Date().setHours(0, 0, 0, 0),
        elaborationEnd: new Date().setHours(0, 0, 0, 0),
        dateTypeFilter: 'administracion',
        selectedArea: '' ,
        selectedInstitution: '' ,
        selectedDrug: '',
        selectedDispatchTime: '',
        stateSearch: []
    });

    const handleNavigate = useCallback(
        (url: string) => {
            navigate(url);
        },
        [navigate]
    );
    const getData = async ( skip = 0 , refresh = false , exportFile = false ) => {
        if ( !refresh ) {
            if (loadingState) return;
            setLoadingState(true);
        }
        try {
            
            let params: any = {
                limit: exportFile ? 99999999 : paginationData.limit,
                skip
            };
    
            if (firstCall) {
                setFirstCall(false);
            }
            let andConditions: any = {};
            const searchValuesStorage = JSON.parse(localStorage.getItem('searchValues') || '{}');
            const stagesListStorage = JSON.parse(localStorage.getItem('stagesList') || '[]');
            if (searchValuesStorage.selectedArea) {
                andConditions["currentStage"] = searchValuesStorage.selectedArea.id;
            } else if (stagesListStorage.length > 0) {
                andConditions["currentStage"] = { "$in": stagesListStorage.map((s: any) => s.id) };
            }
            let dateValues = firstCall ? searchValues : JSON.parse(localStorage.getItem('searchValues') || '{}');
            if (dateValues.dateTypeFilter === 'elaboracion') {
                const { adjustedStartDate, adjustedEndDate } = adjustStartAndEndDate(
                    new Date(dateValues.elaborationStart),
                    new Date(dateValues.elaborationEnd)
                );
                andConditions["elaborationDate"] = {
                    "$gte": adjustedStartDate,
                    "$lte": adjustedEndDate
                };
            } else {
                const { adjustedStartDate, adjustedEndDate } = adjustStartAndEndDate(
                    new Date(dateValues.administrationStart),
                    new Date(dateValues.administrationEnd)
                );
                andConditions["medications.administrationDate"] = {
                    "$gte": adjustedStartDate,
                    "$lte": adjustedEndDate
                };
            }
            const combinations: any = [];

            // Comprobar si hay filtros de paciente y estado en el almacenamiento
            if (searchValuesStorage.patientSearch?.length > 0 && searchValuesStorage.stateSearch?.length > 0) {
                searchValuesStorage.patientSearch.forEach((patient: string) => {
                    searchValuesStorage.stateSearch.forEach((state: string) => {
                        combinations.push({
                            "patient.fullName": { "$regex": patient, "$options": "i" },
                            "status": state
                        });
                    });
                });
                andConditions["$or"] = combinations;
            } else {
                if (searchValuesStorage.patientSearch?.length > 0) {
                    andConditions["$or"] = searchValuesStorage.patientSearch.map((searchTerm: string) => ({
                        "patient.fullName": { "$regex": searchTerm, "$options": "i" }
                    }));
                }
                if (searchValuesStorage.stateSearch?.length > 0) {
                    andConditions["$or"] = [
                        ...(andConditions["$or"] || []),
                        ...searchValuesStorage.stateSearch.map((state: string) => ({
                            "status": state
                        }))
                    ];
                }
            }
    
            if (searchValuesStorage.selectedDrug) {
                andConditions["medications.drugId"] = Number(searchValuesStorage.selectedDrug.id);
            }
            if (searchValuesStorage.selectedInstitution) {
                andConditions["organizationId"] = searchValuesStorage.selectedInstitution.idOrg;
            }

            if (Object.keys(andConditions).length > 0) {
                params = { ...params, ...andConditions };
            }
    
            const getListTask = async () => {
                const responseListTask = await getTasks(params);
                if (responseListTask) {
                    if ( exportFile ) {
                        reportAutorizationExcel(
                            responseListTask.data.tasks ,
                            responseListTask.data.metadata.totalCount ,
                            dateValues.dateTypeFilter === 'elaboracion' ? new Date(dateValues.elaborationStart) : new Date(dateValues.administrationStart) ,
                            dateValues.dateTypeFilter === 'elaboracion' ? new Date(dateValues.elaborationEnd) : new Date(dateValues.administrationEnd) ,
                            user
                        )
                    } else {
                        setPaginationData({
                            limit: responseListTask.data.metadata.limit,
                            skip: responseListTask.data.metadata.skip,
                            totalCount: responseListTask.data.metadata.totalCount
                        });
                        const listTEMP = responseListTask.data.tasks.map((task: any) => {
                            task.CHECKED = false;
                            return task;
                        });
                        setTasksList(listTEMP);
                    }
                }
            };
            await getListTask();
        } catch (error: any) {
            setAlertToastIcon('error');
            setAlertToastMessage(error.response?.data?.error?.message ?? 'Error en la búsqueda');
            setAlertToastState(true);
            if (error.response) {
                if ( error.response.status === 401 ) {
                    setNoAccess( true )
                }
            }
        } finally {
            setLoadingState(false);
        }
    };
    
    useAutoRefresh(() => {
        getData( paginationData.skip , true )
    });

    useEffect(() => {
        getData();
    }, [
        searchValues.patientSearch ,
        searchValues.administrationEnd ,
        searchValues.administrationStart ,
        searchValues.elaborationEnd ,
        searchValues.elaborationStart ,
        searchValues.dateTypeFilter ,
        searchValues.selectedService ,
        searchValues.selectedArea ,
        searchValues.selectedInstitution ,
        searchValues.selectedDrug ,
        searchValues.stateSearch ,
        searchValues.selectedDispatchTime
    ]);

    useEffect(() => {
        setOptionMenu('reportAutorization');
        setBreadcrumb({ name: 'Reporte Autorización', active: true });
        localStorage.removeItem( btoa('editingPatient') )
        getDataFilter();
    }, [] );

    const changePagination = (pageNumber: number) => {
        const limit = paginationData.limit;
        const newSkip = (pageNumber - 1) * limit;
        setPaginationData({
            ...paginationData,
            skip: newSkip
        });
        getData(newSkip);
    };

    const handleExport = () => {
        getData( 0 , false , true )
    }

    const getDataFilter = async () => {
        try {
            const decodedToken = token ? jwtDecode<DecodedToken>(token) : null;
            const institutionId = decodedToken?.org_code;
    
            if (!institutionId) {
                throw new Error("No se pudo obtener el org_code del token.");
            }
            const fetchServices = async () => {
                const [ allStagesResponse , institutionsResponse , drugsResponse ] = await Promise.all([
                    getAllStages( decodedToken.organization_properties.workflow_id.v ),
                    getInstitutions(),
                    getAllDrugs(),
                ]);

                if ( allStagesResponse ) {
                    setStagesList( allStagesResponse.data );
                    localStorage.setItem('stagesList', JSON.stringify(allStagesResponse.data));
                }

                if ( institutionsResponse ) {
                    setInstitutionsList( institutionsResponse.data );
                }

                if ( drugsResponse ) {
                    setDrugsList( drugsResponse.data );
                }
            };
            await fetchServices();
        } catch (error: any) {
            if (error?.response && error.response?.status === 401) {
                setAlertToastMessage( 'Su sesión no tiene los permisos necesarios para esta acción.' );
                setAlertToastIcon( 'error' );
                setAlertToastState( true );
            } else {
                setAlertToastIcon('error');
                setAlertToastMessage('Error al obtener la información de los filtros.');
                setAlertToastState(true);
            }
            console.error("Error fetching services or states:", error);
        } finally {
            setLoadingState(false);
        }
    };
    
    const applySetSearchValues = (values: any) => {
        setSearchValues(values);
    }

    useEffect(() => {
        getData();
    }, [
        searchValues.selectedInstitution ,
        searchValues.selectedDrug ,
        searchValues.stateSearch
    ]);

    return <>
        {loadingState && <LoadingScreen message={loadingMessage} />}
        { alertToastState && (
            <AlertToast icon={alertToastIcon} title={alertToastMessage} closeAlert={() => setAlertToastState(false)} />
        )}
        {
            !noAccess ? <>
                <h1 className="titlePage">Reporte de Autorización</h1>
                <div className="bodyContent">
                    <FilterConciliation
                        stagesList={ stagesList }
                        searchValues={ searchValues }
                        institutionsList={ institutionsList }
                        drugsList={ drugsList }
                        handleGetData={ getData }
                        handleSetSearchValues={ applySetSearchValues }
                        handleSetAdministrationDateRange={setAdministrationDateRange}
                    />

                    <div className={ styles.exportBox }>
                        <div className={ styles['exportBox-countBox'] }>Cantidad de registros: { paginationData.totalCount }</div>
                        <Button
                            label="Exportar"
                            type="primary"
                            icon="xls"
                            iconPosition="left"
                            handleClick={handleExport}
                            disabled={tasksList.length === 0}
                        />
                    </div>


                    { tasksList.length === 0 ? (
                        <div className="alert alert-info text-center" role="alert">
                            No se encontraron solicitudes
                        </div>
                    ) : <>
                        <TableReportAutorization data={tasksList} />

                        <PaginationComponent
                            currentPage={Math.floor(paginationData.skip / paginationData.limit) + 1}
                            totalPages={Math.ceil(paginationData.totalCount / paginationData.limit)}
                            onPageChange={changePagination}
                        />
                    </>}
                </div>
            </> : <NoAccess />
        }
    </>;
};

export default ReportAutorizationPage;
